/**
 * DailyReflectionFormV2 — V2 port of the Daily Inspiration edit
 * form. Same Redux fetch / submit graph as v1
 * (Pages/DailyReflectionPage/DailyReflectionForm.js):
 *
 *   • fetchOneDailyReflection → state.daily_reflections.selectedDailyReflection
 *   • addEditDailyReflections(payload, navigate)
 *   • deleteDailyReflection(id, navigate)
 *
 * Sections:
 *   01 Basic info    — scheduled date / title (req) / author
 *   02 Media         — deeplink image + audio (auto-detects length)
 *   03 Universal link (edit-only, when issued)
 *   04 Tags          — TagsSection (edit-only, v1 sub-component
 *                      kept intact since it manages its own state
 *                      + auto-add/remove flow)
 *   05 Danger zone (edit-only)
 *
 * The page chrome owns the sticky header (eyebrow + title + Save).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getUnixTime } from 'date-fns';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import TagsSection from '../../../Common/TagsSection';
import {
  addEditDailyReflections,
  fetchOneDailyReflection,
  deleteDailyReflection,
} from '../../../../actions/dailyReflectionActions';
import { uploadAudio, uploadImage } from '../../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';

import { Button } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  LinkField,
  UploadTile,
  AudioTile,
  AuthorSelectButton,
  ToggleSwitch,
  DatePicker,
} from '../../../redesign/edit';

const DailyReflectionFormV2 = forwardRef(function DailyReflectionFormV2(
  { onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const mediaAudio = useSelector((s) => s.media?.audio);
  const mediaImage = useSelector((s) => s.media?.image);
  const loading = useSelector((s) => s.daily_reflections?.loading);
  const selectedDailyReflection = useSelector(
    (s) => s.daily_reflections?.selectedDailyReflection,
  );

  const [dailyReflection, setDailyReflection] = useState({
    date: '',
    title: '',
    author_id: '',
    deeplink_image: null,
    deeplink_image_url: '',
    audio_url: '',
    audio_length: 0,
    is_seasonal: false,
  });
  const [tags, setTags] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch + hydrate (1:1 with v1) ──────────────────────── */
  useEffect(() => {
    if (!id) return;
    if (selectedDailyReflection) {
      const next = {
        ...selectedDailyReflection,
        date: new Date(selectedDailyReflection.date_epoch * 1000),
        author_id: selectedDailyReflection.author_id || '',
        is_seasonal: Boolean(selectedDailyReflection.is_seasonal),
      };
      setDailyReflection(next);
      if (selectedDailyReflection.tags?.length > 0) {
        setTags(selectedDailyReflection.tags.map((t) => ({ tag: t.tag })));
      }
      if (onLoadedRef.current) {
        onLoadedRef.current({
          title: selectedDailyReflection.title,
          updated_at:
            selectedDailyReflection.updated_at
            ?? selectedDailyReflection.daily_reflection_updated_at
            ?? null,
        });
      }
      return;
    }
    dispatch(fetchOneDailyReflection(id));
  }, [id, selectedDailyReflection, dispatch]);

  /* ─── Media settle (1:1 with v1) ────────────────────────── */
  useEffect(() => {
    if (mediaImage) {
      setDailyReflection((prev) => ({ ...prev, deeplink_image_url: mediaImage }));
    }
  }, [mediaImage]);

  useEffect(() => {
    if (mediaAudio) {
      setDailyReflection((prev) => ({ ...prev, audio_url: mediaAudio }));
    }
  }, [mediaAudio]);

  /* ─── Handlers ─────────────────────────────────────────── */
  const setField = (name, value) =>
    setDailyReflection((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = (e) => setField(e.target.name, e.target.value);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setField('deeplink_image', file);
    dispatch(uploadImage(file));
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(uploadAudio(file));
  };

  const updateDuration = (e) => {
    const duration = Math.floor(e.target.duration) || 0;
    setField('audio_length', duration);
  };

  const handleSave = useCallback(() => {
    setIsSaving(true);
    /* Daily Inspirations now recur annually (Monday 12107892350) — we
     * derive (schedule_month, schedule_day) from the picked date and
     * discard year. date_epoch is sent along for the API transition;
     * the APIv2 service treats (month, day) as the source of truth. */
    const data = {
      ...dailyReflection,
      date_epoch: dailyReflection.date ? getUnixTime(dailyReflection.date) : null,
      schedule_month: dailyReflection.date ? dailyReflection.date.getMonth() + 1 : null,
      schedule_day: dailyReflection.date ? dailyReflection.date.getDate() : null,
      is_seasonal: dailyReflection.is_seasonal ? 1 : 0,
    };
    const result = dispatch(addEditDailyReflections(data, () => navigate('/daily-inspirations')));
    if (result && typeof result.finally === 'function') {
      result.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 3000);
    }
  }, [dailyReflection, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    handleSave();
  };

  useImperativeHandle(ref, () => ({ submit: handleSave }), [handleSave]);

  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this record!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteDailyReflection(
          dailyReflection.daily_reflection_id,
          () => navigate('/daily-inspirations'),
        ));
      }
    });
  };

  const isEdit = !!id;
  const imagePreview = dailyReflection.deeplink_image
    ? URL.createObjectURL(dailyReflection.deeplink_image)
    : dailyReflection.deeplink_image_url;

  return (
    <>
      <LoadingBackdrop open={loading || isSaving} />
      <form
        id="daily-inspiration-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 920 }}
      >
        {/* 01 Basic info */}
        <FormSection title="Basic info" num="01">
          <FieldRow cols={3} minColWidth={200}>
            <FieldBlock
              label="Scheduled for"
              hint="Recurs annually on this month + day (year is ignored). Feb 29 is reserved for the leap-year evergreen pick."
            >
              <DatePicker
                value={dailyReflection.date ? new Date(dailyReflection.date) : null}
                onChange={(date) => setField('date', date)}
                /* Block Feb 29 — that slot is reserved for the
                 * backend's leap-year evergreen-fallback pick
                 * (DailyInspirationRepository::pickEvergreenFallback
                 * on APIv2). Saving (2, 29) would lock the fallback
                 * pool out and is also rejected server-side. JS
                 * months are 0-indexed; February is month 1. */
                shouldDisableDate={(d) => d.getMonth() === 1 && d.getDate() === 29}
              />
            </FieldBlock>
            <FieldBlock label="Title" required htmlFor="f-di-title">
              <TextInput
                id="f-di-title"
                name="title"
                value={dailyReflection.title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Author" required>
              <AuthorSelectButton
                value={dailyReflection.author_id}
                onChange={(value) => setField('author_id', value)}
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {/* 02 Media */}
        <FormSection title="Media" num="02">
          <FieldRow cols={2} minColWidth={240}>
            <UploadTile
              label="Deeplink image"
              hint="Social share card"
              value={imagePreview}
              accept="image/*"
              onChange={handleImageUpload}
              onRemove={() => setDailyReflection((prev) => ({
                ...prev,
                deeplink_image: null,
                deeplink_image_url: null,
              }))}
            />
            <AudioTile
              label="Audio"
              hint="Auto-detects duration after upload"
              value={dailyReflection.audio_url}
              onChange={handleAudioUpload}
              onLoadedMetadata={updateDuration}
              onRemove={() => setField('audio_url', null)}
            />
          </FieldRow>
          {dailyReflection.universal_link && (
            <div style={{ marginTop: 16 }}>
              <FieldBlock
                label="Universal Link"
                hint="Generated automatically · used for share & deep links"
                noMargin
              >
                <LinkField value={dailyReflection.universal_link} />
              </FieldBlock>
            </div>
          )}
        </FormSection>

        {/* 03 Settings */}
        <FormSection
          title="Settings"
          num="03"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              Scheduling behavior
            </span>
          }
        >
          <FieldBlock
            label="Seasonal"
            hint="Tied to a holiday or season (e.g. New Year). Excluded from the Feb 29 fallback pool on leap years."
          >
            <ToggleSwitch
              checked={Boolean(dailyReflection.is_seasonal)}
              onChange={(e) => setField('is_seasonal', e.target.checked)}
              label={dailyReflection.is_seasonal ? 'Seasonal' : 'Not seasonal'}
            />
          </FieldBlock>
        </FormSection>

        {/* 04 Tags (edit-only) */}
        {dailyReflection.daily_reflection_id > 0 && (
          <FormSection
            title="Tags"
            num="04"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                Auto-applied on add/remove
              </span>
            }
          >
            <TagsSection
              tags={tags}
              onTagsChange={setTags}
              onTagsRemove={() => {}}
              objectId={dailyReflection.daily_reflection_id}
              objectType="DailyInspiration"
              title=""
            />
          </FormSection>
        )}

        {/* 05 Danger zone (04 in create mode since Tags is edit-only) */}
        {isEdit && (
          <FormSection
            title="Danger zone"
            num={dailyReflection.daily_reflection_id > 0 ? '05' : '04'}
          >
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete daily inspiration
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default DailyReflectionFormV2;
